import { api } from "./api.service";

export type AppointmentStatus =
  | "PENDENTE"
  | "CONFIRMADO"
  | "CANCELADO"
  | "CONCLUIDO"
  | "PAGAMENTO_PENDENTE"
  | "PAGAMENTO_PROCESADO"
  | string;

export interface AppointmentsReportFilters {
  start: string | null;
  end: string | null;
  status: string | null;
}

export interface AppointmentsReportTotals {
  appointments: number;
  patients: number;
  doctors: number;
  availableHours?: number;
}

export interface PaginationMeta {
  page: number;
  perPage: number;
  totalItems: number;
  totalPages: number;
}

export interface AppointmentsReportItem {
  id: string;
  date: string; // ISO
  status: string;
  patient: {
    id: string; // id do usuário/paciente
    name: string | null;
  } | null;
  doctor: {
    id: string;      // id do médico (doctorId)
    userId?: string | null; // id do usuário vinculado ao médico, se o backend enviar
    name: string | null;
  } | null;
  local: {
    id: string;
    localName?: string | null;
    address?: string | null;
    city?: string | null;
    state?: string | null;
    zip_code?: string | null;
    phone?: string | null;
  } | null;
}

export interface AppointmentsReportResponse {
  filters: AppointmentsReportFilters;
  totals: AppointmentsReportTotals;
  appointments: AppointmentsReportItem[];
  pagination: PaginationMeta;
}

// ---------------- Relatório de Médicos ----------------
export interface DoctorsReportFilters {
  start: string | null;
  end: string | null;
  createdStart: string | null;
  createdEnd: string | null;
}

export interface DoctorsReportTotals {
  doctors: number;
  appointments: number;
  completedAppointments: number;
  availableHours: number;
}

export interface DoctorsReportClinic {
  id: string;
  localName: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip_code: string | null;
  phone: string | null;
}

export interface DoctorsReportItem {
  id: string;
  userId: string;
  name: string;
  createdAt: string; // ISO
  clinics: DoctorsReportClinic[];
  appointmentsCount: number;
  completedAppointmentsCount: number;
  availableHours: number;
}

export interface DoctorsReportResponse {
  filters: DoctorsReportFilters;
  totals: DoctorsReportTotals;
  doctors: DoctorsReportItem[];
  pagination: PaginationMeta;
}

// ---------------- Relatório de Pacientes ----------------
export interface PatientsReportTotals {
  patients: number;
  socialProgram: number;
  withAppointments: number;
}

export interface PatientsReportItem {
  id: string;
  name: string;
  createdAt: string; // ISO - data de cadastro
  level: number | null;
  socialProgram: boolean | null;
  city: string | null;
  state: string | null;
  appointmentsCount: number;
}

export interface PatientsReportResponse {
  totals: PatientsReportTotals;
  patients: PatientsReportItem[];
  pagination: PaginationMeta;
}

export interface GetAppointmentsReportParams {
  start?: string;
  end?: string;
  status?: AppointmentStatus[];
  page?: number;
  perPage?: number;
}

export interface GetDoctorsReportParams {
  start?: string;
  end?: string;
  createdStart?: string;
  createdEnd?: string;
  page?: number;
  perPage?: number;
}

export interface GetPatientsReportParams {
  start?: string;
  end?: string;
  level?: number | string;
  city?: string;
  state?: string;
  page?: number;
  perPage?: number;
}

export async function getAppointmentsReport(
  params: GetAppointmentsReportParams = {}
): Promise<AppointmentsReportResponse> {
  const { start, end, status, page, perPage } = params;

  const { data } = await api.get<AppointmentsReportResponse>("/reports/appointments", {
    params: {
      ...(start ? { start } : {}),
      ...(end ? { end } : {}),
      ...(status && status.length ? { status: status.join(",") } : {}),
      ...(page ? { page } : {}),
      ...(perPage ? { perPage } : {}),
    },
  });

  return data;
}

export async function getDoctorsReport(
  params: GetDoctorsReportParams = {}
): Promise<DoctorsReportResponse> {
  const { start, end, createdStart, createdEnd, page, perPage } = params;

  const { data } = await api.get<DoctorsReportResponse>("/reports/doctors", {
    params: {
      ...(start ? { start } : {}),
      ...(end ? { end } : {}),
      ...(createdStart ? { createdStart } : {}),
      ...(createdEnd ? { createdEnd } : {}),
      ...(page ? { page } : {}),
      ...(perPage ? { perPage } : {}),
    },
  });

  return data;
}

export async function getPatientsReport(
  params: GetPatientsReportParams = {}
): Promise<PatientsReportResponse> {
  const { start, end, level, city, state, page, perPage } = params;

  const { data } = await api.get<PatientsReportResponse>("/reports/patients", {
    params: {
      ...(start ? { start } : {}),
      ...(end ? { end } : {}),
      ...(level !== undefined && level !== null && level !== "" ? { level } : {}),
      ...(city ? { city } : {}),
      ...(state ? { state } : {}),
      ...(page ? { page } : {}),
      ...(perPage ? { perPage } : {}),
    },
  });

  return data;
}
